import { createContext, useContext } from "react";
import { action, computed, observable } from "mobx";
import { IComboBoxProps } from "./ComboBox.component";

export type IComboBoxOption = {
  value: string;
  label: string;
};

export class ComboBoxState {
  @observable
  panelState = false;

  @observable
  options: IComboBoxOption[] = [];

  @observable
  showAll = false;

  /**
   * Value of the typed input
   */
  @observable
  currentInput = "";

  constructor(public props: IComboBoxProps) {
    this.options = props.options || [];
    if (props.defaultValue) {
      this.showAll = true;
    }
    const matchedDefaultValue = this.options.find(
      (opt) => opt.value === (props.defaultValue || props.value)
    );
    this.currentInput = matchedDefaultValue?.label || "";
  }

  @action
  onSelect = (option: IComboBoxOption) => {
    this.currentInput = option.label;
    this.props.onChange?.(option);
    this.panelState = false;
    this.showAll = false;
  };

  @action
  onInputFocus = () => {
    this.panelState = true;
  };

  @computed
  get filteredOptions(): IComboBoxOption[] {
    if (this.props.dropdown || this.showAll) {
      return this.options;
    }

    // Removes non alphanumeric chars: PT. Bank Central Asia (Tbk) -> PT Bank Central Asia Tbk
    const sanitizeInput = this.currentInput.replace(/[\W_]+/g, " ");

    return this.options
      .filter((option) => {
        // Removes non alphanumeric chars: PT. Bank Central Asia (Tbk) -> PT Bank Central Asia Tbk
        const sanitizeLabel = option.label.replace(/[\W_]+/g, " ");
        // PT Bank Central Asia Tbk -> [PT, Bank, Central, Asia, Tbk]
        const splitLabelBySpace = sanitizeLabel.split(" ");
        // [PT, Bank, Central, Asia, Tbk] -> [P, B, C, A, T]
        const firstLetterOfLabels = splitLabelBySpace.map((word) => word[0]);
        // [P, B, C, A] -> PBCAT
        const labelAbbreviation = firstLetterOfLabels.join("");

        // matches input with abbreviation
        const matchByAbbreviation = labelAbbreviation.match(
          new RegExp(sanitizeInput, "i")
        );

        // matches input with option.label
        const matchByInput = sanitizeLabel.match(
          new RegExp(sanitizeInput, "i")
        );

        // matches input with option.value
        const matchByValue = option.value.match(new RegExp(sanitizeInput, "i"));

        return matchByAbbreviation || matchByInput || matchByValue;
        // Show a maximum of 50 options at once
      })
      .slice(0, 100);
  }
}

export const ComboBoxContext = createContext(new ComboBoxState({} as any));

export const useComboBoxContext = () => useContext(ComboBoxContext);
